import { useMemo } from 'react';
import { AppLink } from '@/shared/components/AppLink';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/shared/widgets/carousel';
import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { getCenterSlideIndex } from '@/shared/utils/carouselUtils';

interface InfoCardItem {
  position?: number;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  btn?: string;
  lazyload?: string;
  shouldRefresh?: boolean;
}

interface InfoCardSectionProps {
  data: Record<string, unknown> & {
    title?: string[];
    btn?: string;
    link?: string;
    blankImageUrl?: string;
    items?: InfoCardItem[];
    showTitle?: boolean;
    shouldRefresh?: boolean;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

function InfoCardContent({ item }: { item: InfoCardItem }) {
  return (
    <div
      className="bg-white rounded-lg p-6 flex flex-col h-full"
      style={{ boxShadow: '0 0 15px rgba(0, 0, 0, 0.1)' }}
    >
      <div className="mb-4 flex-shrink-0 flex items-center justify-center h-[70px]">
        <img
          className="w-full h-auto object-contain max-h-16"
          src={resolveImageUrl(item.imageUrl)}
          alt={item.title}
          loading="lazy"
        />
      </div>
      <div className="mb-4 flex-grow text-sm text-gray-700 leading-relaxed text-center">
        {item.description}
      </div>
      {item.btn && (
        <div className="mt-auto text-center">
          <AppLink
            to={item.link}
            rel="noopener nofollow"
            target="_blank"
            className="inline-block px-4 py-2 border border-black rounded text-sm font-medium hover:opacity-80 transition-opacity"
            eventName="News Card Clicked"
            eventProperties={{ 'News Card Name': item.title }}
            shouldRefresh={item.shouldRefresh}
          >
            {item.btn}
          </AppLink>
        </div>
      )}
    </div>
  );
}

export default function InfoCardSection({
  data,
  sectionKey: _sectionKey,
}: InfoCardSectionProps) {
  const items = useMemo(() => (data.items || []) as InfoCardItem[], [data.items]);
  const title = data.title?.[0] || '';
  const link = data.link || '#';
  const btn = data.btn || '';
  const showTitle = data.showTitle !== false;

  // Calculate initial slide index (center slide)
  const initialSlideIndex = useMemo(() => getCenterSlideIndex(items.length), [items.length]);

  // Desktop should show all items
  const desktopItems = items;

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12 w-full">
      {/* Title */}
      {showTitle && title && (
        <div className="container-fluid">
          <h2 className="mb-4 md:mb-0 text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
            <span className="font-primary text-[#9D7792] md:text-[#032C5E]">
              {title}
            </span>
          </h2>
        </div>
      )}

      {/* Desktop: All items */}
      {desktopItems.length > 0 && (
        <div className="hidden md:grid md:grid-cols-4 md:gap-4 container-fluid pt-8 pb-8 max-w-7xl mx-auto">
          {desktopItems.map((item, index) => (
            <div
              key={`desktop-${item.title}-${index}`}
              className="flex flex-col py-2 w-full"
            >
              <InfoCardContent item={item} />
            </div>
          ))}
        </div>
      )}

      {/* Mobile: Carousel - full bleed, 50vw width per slide */}
      {items.length > 0 && (
        <Carousel
          opts={{
            align: 'center',
            loop: false,
            slidesToScroll: 1,
            containScroll: false,
            startIndex: initialSlideIndex,
          }}
          className="w-full md:hidden"
        >
          <CarouselContent className="-ml-0 gap-2.5 py-4">
            {items.map((item, index) => (
              <CarouselItem
                key={`${item.title}-${index}`}
                className="pl-0 basis-auto flex-shrink-0 py-2"
                style={{ width: '50vw', minWidth: '50vw', maxWidth: '50vw' }}
              >
                <InfoCardContent item={item} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}

      {/* View All News Button */}
      {btn && (
        <div className="mt-8 md:mt-0 flex justify-center container-fluid">
          <AppLink
            to={link}
            className="inline-flex items-center justify-center px-6 py-2 rounded border border-[#E85A4F] text-[#E85A4F] font-medium text-sm hover:opacity-90 transition-opacity uppercase"
            eventName="View All News Clicked"
            shouldRefresh={data.shouldRefresh}
          >
            {btn}
          </AppLink>
        </div>
      )}
    </section>
  );
}

